import { Helmet } from 'react-helmet-async';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useWhitepapers } from '@/hooks/useWhitepapers';
import Loader from '@/components/Common/Loader';
import { sendGAPageview } from '../utils/gaConsent';
import SharedModal from '@/components/Modal/SharedModal';
import SharedButton from '@/global/Button/Button';

const CONTACT_API_BASE_URL = process.env.REACT_APP_CONTACT_API_URL || '';

type WhitepaperLeadForm = {
  name: string;
  organisation: string;
  role: string;
  primaryInterestArea: string;
  preferredContact: string;
};

const emptyForm: WhitepaperLeadForm = {
  name: '',
  organisation: '',
  role: '',
  primaryInterestArea: '',
  preferredContact: '',
};

const Whitepapers = () => {
  const { whitepapers, loading, error } = useWhitepapers();
  const [selectedWhitepaperId, setSelectedWhitepaperId] = useState<number | null>(null);
  const [form, setForm] = useState<WhitepaperLeadForm>(emptyForm);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);

  const selectedWhitepaper = useMemo(
    () => whitepapers.find(item => item.id === selectedWhitepaperId) || null,
    [whitepapers, selectedWhitepaperId],
  );

  useEffect(() => {
    sendGAPageview({ hitType: 'pageview', page: '/whitepapers', title: 'Whitepapers' });
  }, []);

  const closeModal = () => {
    setSelectedWhitepaperId(null);
    setForm(emptyForm);
    setFormError('');
    setIsSubmitting(false);
    setRequestSuccess(false);
  };

  const handleOpenForm = (whitepaperId: number) => {
    setSelectedWhitepaperId(whitepaperId);
    setForm(emptyForm);
    setFormError('');
    setRequestSuccess(false);
  };

  const onSubmitWhitepaperForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!CONTACT_API_BASE_URL) {
      setFormError('Whitepaper form backend is not configured. Please contact support.');
      return;
    }

    setIsSubmitting(true);
    setFormError('');

    try {
      const response = await fetch(`${CONTACT_API_BASE_URL}/whitepaper/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          whitepaperTitle: selectedWhitepaper.title,
          pdfUrl: selectedWhitepaper.pdfUrl || selectedWhitepaper.link,
        }),
      });

      if (!response.ok) {
        let backendError = '';
        try {
          const errorPayload = await response.json();
          backendError = String(errorPayload?.error || errorPayload?.message || '');
        } catch {
          // Ignore parse error and use default message.
        }

        if (backendError.toLowerCase().includes('disposable email')) {
          throw new Error('Disposable email addresses are not allowed. Please use your institutional email.');
        }

        throw new Error(backendError || 'Unable to process whitepaper request');
      }

      setRequestSuccess(true);
    } catch (submissionError) {
      console.error('Whitepaper request error:', submissionError);
      setFormError(
        submissionError instanceof Error
          ? submissionError.message
          : 'We could not process your request. Please try again in a moment.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formContent = selectedWhitepaper ? (
    <div className="mt-4">
      {!requestSuccess ? (
        <form className="space-y-4" onSubmit={onSubmitWhitepaperForm}>
          <p className="text-sm text-slate-600">
            Complete the form and we will send the PDF link to your email.
          </p>
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Name"
            value={form.name}
            onChange={event => setForm(previous => ({ ...previous, name: event.target.value }))}
            required
          />
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Organisation"
            value={form.organisation}
            onChange={event => setForm(previous => ({ ...previous, organisation: event.target.value }))}
            required
          />
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Role"
            value={form.role}
            onChange={event => setForm(previous => ({ ...previous, role: event.target.value }))}
            required
          />
          <input
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Primary interest area"
            value={form.primaryInterestArea}
            onChange={event =>
              setForm(previous => ({ ...previous, primaryInterestArea: event.target.value }))
            }
            required
          />
          <input
            type="email"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            placeholder="Preferred contact (email)"
            value={form.preferredContact}
            onChange={event => setForm(previous => ({ ...previous, preferredContact: event.target.value }))}
            required
          />
          {formError ? <p className="text-sm text-red-600">{formError}</p> : null}
          <SharedButton type="submit" variant="primary" size="sm" disabled={isSubmitting} className="ml-0">
            {isSubmitting ? 'Sending...' : 'Send me the PDF link'}
          </SharedButton>
        </form>
      ) : (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          We have sent you an email with the download link.
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
      <Helmet>
        <title>DISGENET: Whitepapers</title>
        <meta
          name="description"
          content="Explore DISGENET whitepapers with biomedical and scientific insights from our team."
        />
        <meta property="og:title" content="DISGENET: Whitepapers" />
        <meta
          property="og:description"
          content="Explore DISGENET whitepapers with biomedical and scientific insights from our team."
        />
        <meta property="og:url" content="https://www.disgenet.com/whitepapers" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/whitepapers" />
      </Helmet>

      <section className="landing-surface-b border-b border-slate-200/80 py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-secondary">
              Resource library
            </p>
            <h1 className="mt-4 text-4xl font-bold tracking-tight text-blue sm:text-5xl lg:text-[3.15rem] lg:leading-tight">
              Whitepapers for
              <span className="text-pink"> biomedical knowledge</span>
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-600 dark:text-slate-400 sm:text-lg">
              Discover technical and scientific whitepapers from MedBioinformatics and DISGENET.
            </p>
          </div>
        </div>
      </section>

      <section className="landing-section landing-surface-a pt-5">
        {process.env.REACT_APP_SHOW_WHITEPAPERS === 'true' ? <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="py-12">
              <Loader text="Loading whitepapers..." isInnerSpinner />
            </div>
          ) : null}

          {!loading && error ? (
            <div className="landing-card-dark border border-red-200/70 bg-red-50/70 p-6 text-sm text-red-700 dark:border-red-400/30 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </div>
          ) : null}

          {!loading && !error ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {whitepapers.map(whitepaper => (
                <article
                  key={whitepaper.id}
                  className="landing-card-dark brand-glow-card brand-hover-card flex h-full flex-col border border-slate-200/70 p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl dark:border-white/10 dark:bg-slate-900/55"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-secondary">
                    {whitepaper.date}
                  </p>
                  <h2 className="mt-3 text-2xl font-bold tracking-tight text-blue">{whitepaper.title}</h2>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                    {whitepaper.description || 'Read this whitepaper to see all details.'}
                  </p>

                  <div className="mt-auto pt-8">
                    <SharedButton
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => handleOpenForm(whitepaper.id)}
                      className="ml-0"
                    >
                      Read whitepaper
                    </SharedButton>
                  </div>
                </article>
              ))}
            </div>
          ) : null}
        </div> :
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1>Coming soon</h1>
        </div>
        }
      </section>

      <SharedModal
        open={!!selectedWhitepaper}
        setOpen={open => {
          if (!open) closeModal();
        }}
        title={selectedWhitepaper ? `Get PDF - ${selectedWhitepaper.title}` : 'Get PDF'}
        content={formContent}
      />
    </>
  );
};

export default Whitepapers;
